import {useCallback, useLayoutEffect, useRef} from 'react';

import afterFrame from '../../afterFrame';
import {recordRumDurationVital} from '../datadogRum';

export type RunToggleState = 'checked' | 'unchecked' | 'partial' | 'unknown';

type RunToggleAction = 'hide run' | 'show run';

type PendingClick = {
  action: RunToggleAction;
  prevState: RunToggleState;
  startMs: number;
};

export function useRunTogglePaintVital(
  state: RunToggleState
): (info: {action: RunToggleAction; prevState: RunToggleState}) => void {
  const pendingClickRef = useRef<PendingClick | null>(null);

  useLayoutEffect(() => {
    const pendingClick = pendingClickRef.current;
    if (pendingClick == null) {
      return;
    }

    pendingClickRef.current = null;
    if (state === pendingClick.prevState) {
      return;
    }

    // Recorded retrospectively (not start/stop) so backgrounded views don't
    // skew the vital.
    afterFrame(afterPaintTimestamp => {
      recordRumDurationVital('workspace_run_toggle_icon_painted', {
        startTime: performance.timeOrigin + pendingClick.startMs,
        duration: afterPaintTimestamp - pendingClick.startMs,
        description: 'run toggle click to icon painted',
        context: {
          action: pendingClick.action,
          nextState: state,
          prevState: pendingClick.prevState,
        },
      });
    });
  });

  return useCallback(({action, prevState}) => {
    pendingClickRef.current = {
      action,
      prevState,
      startMs: performance.now(),
    };
  }, []);
}
